import { useForm } from '@inertiajs/react'
import type { FormEvent } from 'react'
import AccountLayout from '@/layouts/AccountLayout'
import type { SurveyTemplateOption } from '@/types'
import { SURVEY_TYPE_LABELS } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'

interface NewProps {
  templates: SurveyTemplateOption[]
}

export default function New({ templates }: NewProps) {
  const ap = useAccountPath()
  const { data, setData, post, processing, errors } = useForm({
    name: '',
    programme_name: '',
    survey_type: 'programme_evaluation',
    anonymity: 'anonymous',
    survey_template_id: templates[0]?.id?.toString() ?? '',
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post(ap('/surveys'))
  }

  const selectedTemplate = templates.find((t) => t.id.toString() === data.survey_template_id)

  return (
    <AccountLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">New Survey</h1>
          <p className="text-base-content/60 mt-1">
            Set up your impact survey. You can customise questions after creation.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="card bg-base-100">
            <div className="card-body space-y-4">
              {Object.keys(errors).length > 0 && (
                <div role="alert" className="alert alert-soft alert-error text-sm">
                  <ul className="list-disc list-inside">
                    {Object.values(errors).map((error, i) => (
                      <li key={i}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Survey name</legend>
                <input
                  type="text"
                  className="input w-full"
                  value={data.name}
                  onChange={(e) => setData('name', e.target.value)}
                  placeholder="e.g. Q1 2026 Leadership Programme"
                  required
                  autoFocus
                />
                <p className="label text-xs whitespace-normal">
                  Internal only — not shown to respondents
                </p>
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Programme / engagement name</legend>
                <input
                  type="text"
                  className="input w-full"
                  value={data.programme_name}
                  onChange={(e) => setData('programme_name', e.target.value)}
                  placeholder="e.g. Senior Leadership Programme — Cohort 3"
                  required
                />
                <p className="label text-xs whitespace-normal">
                  Shown to respondents as context for the survey
                </p>
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Survey type</legend>
                <select
                  className="select w-full"
                  value={data.survey_type}
                  onChange={(e) => setData('survey_type', e.target.value)}
                  required
                >
                  {Object.entries(SURVEY_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Respondent anonymity</legend>
                <div className="grid gap-2">
                  <label className="flex items-start gap-3 rounded-lg border border-base-300 p-3 cursor-pointer has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <input
                      type="radio"
                      name="anonymity"
                      className="radio radio-sm mt-0.5"
                      value="anonymous"
                      checked={data.anonymity === 'anonymous'}
                      onChange={(e) => setData('anonymity', e.target.value)}
                      required
                    />
                    <span>
                      <span className="flex items-center gap-2 font-medium">
                        Anonymous
                        <span className="badge badge-soft badge-primary badge-sm">Recommended</span>
                      </span>
                      <span className="text-xs text-base-content/60">No respondent identity is ever stored. Best for candid feedback.</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 rounded-lg border border-base-300 p-3 cursor-pointer has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <input
                      type="radio"
                      name="anonymity"
                      className="radio radio-sm mt-0.5"
                      value="named"
                      checked={data.anonymity === 'named'}
                      onChange={(e) => setData('anonymity', e.target.value)}
                      required
                    />
                    <span>
                      <span className="block font-medium">Named</span>
                      <span className="text-xs text-base-content/60">Capture each respondent's name and email (with disclosure). Enables reminders, per-person tracking, and integrations.</span>
                    </span>
                  </label>
                </div>
                <p className="label text-xs whitespace-normal">You can change this until the first response arrives — then it locks.</p>
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Template</legend>
                <select
                  className="select w-full"
                  value={data.survey_template_id}
                  onChange={(e) => setData('survey_template_id', e.target.value)}
                  required
                >
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} {t.question_count > 0 ? `(${t.question_count} questions)` : '(blank)'}
                    </option>
                  ))}
                </select>
                {selectedTemplate?.description && (
                  <p className="label text-xs whitespace-normal">{selectedTemplate.description}</p>
                )}
              </fieldset>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <a href={ap('/surveys')} className="btn btn-ghost btn-sm">Cancel</a>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={processing}
            >
              {processing && <span className="loading loading-spinner loading-sm" />}
              Create survey
            </button>
          </div>
        </form>
      </div>
    </AccountLayout>
  )
}
